/**
 * Reading what the claimant types (D643): deterministic, offline, country-neutral. The country pack supplies how a document, a date and a phone
 * number are written (`country.ts`); everything else here is language handling over the platform's four content languages. Nothing in this file
 * stores or logs a value: it returns what it found and the caller digests it at once.
 */
import { birthDateOf, sameCalendarDay } from './country.js';
import { foldName, nameCandidate, nameTokens } from './name.js';
import { VELO_CONTENT_LANGUAGES } from './templates.js';
const fold = foldName;
const YES = new Set(['sim', 's', 'si', 'yes', 'y', 'ok', 'claro', 'pode', 'certo', 'isso', 'esse', 'este', 'exato', 'correto', 'yep', 'yeah', 'oui', 'vale', 'dale', 'va', 'bene']);
const NO = new Set(['nao', 'no', 'n', 'non', 'nope', 'nunca', 'jamais', 'never', 'mai']);
/** A yes/no reading of a short reply: a negation anywhere is a no; a yes must be the whole reply. */
export function readYesNo(text) {
    const t = nameTokens(fold(text));
    if (t.length === 0 || t.length > 4)
        return 'unclear';
    if (t.some((x) => NO.has(x)))
        return 'no';
    return t.every((x) => YES.has(x) || x === 'por' || x === 'favor' || x === 'please' || x === 'bene') && t.some((x) => YES.has(x)) ? 'yes' : 'unclear';
}
const CANCEL = ['cancelar', 'cancela', 'cancel', 'cancelo', 'annulla', 'annullare', 'desistir', 'desisto', 'desistiu', 'esquece', 'esquecer', 'forget it', 'nao quero mais', 'no quiero mas', 'non voglio piu', 'stop', 'parar'];
export function isCancel(text) {
    const f = fold(text);
    return f.length > 0 && f.length <= 40 && CANCEL.some((c) => f === c || f.startsWith(`${c} `) || f.endsWith(` ${c}`));
}
const RESEND = ['reenviar', 'reenvia', 'reenvie', 'resend', 'reinvia', 'reinviare', 'reenviame', 'enviar de novo', 'enviar novamente', 'novo codigo', 'new code', 'otro codigo', 'nuovo codice'];
export function isResend(text) {
    const f = fold(text);
    return f.length > 0 && f.length <= 40 && RESEND.some((c) => f === c || f.startsWith(`${c} `) || f.endsWith(` ${c}`));
}
const DONE = ['pronto', 'feito', 'done', 'listo', 'fatto', 'terminei', 'finished', 'ya', 'ok pronto', 'enviei', 'tirei', 'sent', 'enviado'];
export function isDone(text) {
    const f = fold(text);
    return f.length > 0 && f.length <= 30 && DONE.some((c) => f === c || f.startsWith(`${c} `) || f.endsWith(` ${c}`));
}
/** A six-digit one-time code (a space or hyphen in the middle is allowed), alone in the message and not part of a longer number. */
export function readCode(text) {
    const m = /(?<!\d)(\d{3})[ -]?(\d{3})(?!\d)/.exec(text);
    if (m === null)
        return undefined;
    const code = `${m[1]}${m[2]}`;
    // A message that carries other numbers is not a code reply (a phone number or a date holds six digits too).
    const others = text.replace(m[0], ' ').match(/\d/g);
    return others === null ? code : undefined;
}
// ---- the identity triple ----------------------------------------------------------------------------------------------------------
const LABEL_WORDS = [
    // names
    'nome', 'name', 'nombre', 'completo', 'complete', 'full', 'completa', 'cognome', 'apellido', 'sobrenome', 'surname', 'last', 'first', 'primeiro',
    // birth
    'nascimento', 'nasc', 'nascido', 'nascida', 'birth', 'birthday', 'born', 'dob', 'nacimiento', 'nacido', 'nacida', 'nascita', 'nato', 'nata', 'data', 'date', 'fecha', 'dia', 'day', 'giorno',
    // glue
    'de', 'del', 'of', 'di', 'em', 'in', 'en', 'on', 'is', 'my', 'mi', 'me', 'meu', 'minha', 'mio', 'mia', 'eu', 'sou', 'soy', 'sono', 'am', 'i', 'e', 'y', 'and', 'et', 'chiamo', 'llamo', 'es', 'llama', 'o', 'a', 'el', 'la', 'il', 'lo',
    // document
    'documento', 'document', 'documento', 'numero', 'number', 'numero', 'num', 'n', 'id', 'dni',
];
export function readIdentity(pack, text, now) {
    const document = pack.document.extract(text);
    const birthDate = birthDateOf(pack, text, now);
    // The name: the words of the message that are not a label, a glue word or a number.
    const stop = new Set([...LABEL_WORDS, ...VELO_CONTENT_LANGUAGES.flatMap((l) => nameTokens(fold(pack.document.labels[l])))]);
    const words = text.split(/[\s,;:|/\\]+/u).filter((w) => w !== '' && !/\d/.test(w));
    const kept = words.map((w) => fold(w)).flatMap((w) => nameTokens(w)).filter((w) => !stop.has(w));
    const candidate = nameCandidate(kept.join(' '), [], []);
    const nameTokensFound = candidate === undefined ? undefined : nameTokens(candidate);
    return {
        ...(document !== undefined ? { document } : {}),
        ...(birthDate !== undefined ? { birthDate } : {}),
        ...(nameTokensFound !== undefined && nameTokensFound.length >= 2 ? { nameTokens: nameTokensFound } : {}),
    };
}
// ---- the knowledge answers --------------------------------------------------------------------------------------------------------
const ANSWER_GLUE = new Set([...LABEL_WORDS, 'foi', 'was', 'fue', 'fu', 'na', 'no', 'em', 'cidade', 'city', 'ciudad', 'citta', 'dia', 'day', 'ride', 'corrida', 'viaje', 'viagem', 'corsa', 'ultima', 'ultimo', 'last', 'the', 'la', 'a', 'to', 'at', 'do', 'da', 'dos', 'das', 'en', 'el', 'del']);
/** The answer "which city and which day was your last ride": the first date's day and month, and the folded words left (the city). */
export function readLastRideAnswer(pack, text) {
    const dates = pack.extractDates(text);
    if (dates.length !== 1)
        return undefined;
    const words = text.split(/[\s,;:|/\\.]+/u).filter((w) => w !== '' && !/\d/.test(w));
    const cityWords = words.flatMap((w) => nameTokens(fold(w))).filter((w) => !ANSWER_GLUE.has(w));
    if (cityWords.length === 0)
        return undefined;
    return { date: dates[0], cityWords };
}
/** Does the answer name the same day and the same city as the account's last ride? The words left must BE the city, no more (a list of cities is not an answer). */
export function lastRideMatches(answer, fact) {
    if (!sameCalendarDay(answer.date, fact.occurredOn))
        return false;
    const city = nameTokens(fold(fact.city)).filter((w) => !ANSWER_GLUE.has(w));
    if (city.length === 0 || city.length !== answer.cityWords.length)
        return false;
    return city.every((w, i) => w === answer.cityWords[i]);
}
/** The payment method tokens an answer names. A message that names several is not an answer. */
export function readPaymentAnswer(pack, text) {
    const f = ` ${fold(text)} `;
    const found = [];
    for (const [method, words] of Object.entries(pack.paymentMethodWords)) {
        if (words.some((w) => f.includes(` ${fold(w)} `)))
            found.push(method);
    }
    return found;
}
// ---- masks ------------------------------------------------------------------------------------------------------------------------------
/** `j•••@g•••.com`: enough to recognise the address, not enough to use it. */
export function maskEmail(email) {
    const at = email.lastIndexOf('@');
    if (at <= 0)
        return '•••';
    const local = email.slice(0, at);
    const domain = email.slice(at + 1);
    const dot = domain.lastIndexOf('.');
    const host = dot > 0 ? domain.slice(0, dot) : domain;
    const tld = dot > 0 ? domain.slice(dot) : '';
    return `${local.slice(0, 1)}•••@${host.slice(0, 1)}•••${tld}`;
}
/** The last four digits of a phone number, for a message ("ending 1234"). */
export function last4(phoneE164) {
    return phoneE164.replace(/\D/g, '').slice(-4);
}
/** `+••••1234`: the plus sign and the last four digits only. */
export function maskPhone(phoneE164) {
    return `+••••${last4(phoneE164)}`;
}
//# sourceMappingURL=parse.js.map